import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, formatApiErrorDetail } from "../lib/api";
import { TEST_IDS } from "../constants/testIds";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { Slack, MessageCircle } from "lucide-react";
import { useAuthStore } from "../store/authStore";
import SmartsheetIntegrationSection from "../components/integrations/SmartsheetIntegrationSection";

const EVENT_KEYS = [
  "task_assigned",
  "task_commented",
  "mentioned",
  "invite_accepted",
  "billing_events",
];

export default function Integrations() {
  const { t } = useTranslation();
  const isSuper = useAuthStore((s) => ["super_admin", "ultra_admin"].includes(s.user?.role));
  const [state, setState] = useState({
    slack_webhook_url: "",
    discord_webhook_url: "",
    events: EVENT_KEYS,
  });

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/tenant/integrations");
      setState({
        slack_webhook_url: data.slack_webhook_url || "",
        discord_webhook_url: data.discord_webhook_url || "",
        events: data.events || EVENT_KEYS,
      });
    })();
  }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.patch("/tenant/integrations", state);
      toast.success(t("integrations.saved"));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const send = async (kind) => {
    try {
      await api.post("/tenant/integrations/test", { kind });
      toast.success(t("integrations.test_sent"));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const toggleEvent = (k) => {
    setState((s) => ({
      ...s,
      events: s.events.includes(k)
        ? s.events.filter((x) => x !== k)
        : [...s.events, k],
    }));
  };

  return (
    <div className="space-y-8">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-zinc-500">
          {t("brand")}
        </div>
        <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight text-zinc-950 sm:text-4xl">
          {t("integrations.title")}
        </h1>
        <p className="mt-1 text-sm text-zinc-500">{t("integrations.subtitle")}</p>
      </div>

      <form onSubmit={save} className="max-w-2xl space-y-6">
        <SmartsheetIntegrationSection isSuper={isSuper} />

        <div className="rounded-lg border border-zinc-200 bg-white p-6">
          <div className="mb-4 flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-md bg-zinc-100">
              <Slack className="h-4 w-4 text-zinc-900" strokeWidth={1.75} />
            </div>
            <div className="font-display text-lg font-semibold">Slack</div>
          </div>
          <Label className="text-xs uppercase tracking-wider text-zinc-500">
            Incoming Webhook URL
          </Label>
          <Input
            placeholder="https://hooks.slack.com/services/…"
            value={state.slack_webhook_url}
            onChange={(e) =>
              setState({ ...state, slack_webhook_url: e.target.value })
            }
            data-testid={TEST_IDS.integrationSlackInput}
            className="mt-1 font-mono text-xs"
          />
          <div className="mt-3 flex justify-end">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => send("slack")}
              disabled={!state.slack_webhook_url}
              data-testid={TEST_IDS.integrationSlackTest}
            >
              {t("integrations.send_test")}
            </Button>
          </div>
        </div>

        <div className="rounded-lg border border-zinc-200 bg-white p-6">
          <div className="mb-4 flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-md bg-indigo-50">
              <MessageCircle className="h-4 w-4 text-indigo-700" strokeWidth={1.75} />
            </div>
            <div className="font-display text-lg font-semibold">Discord</div>
          </div>
          <Label className="text-xs uppercase tracking-wider text-zinc-500">
            Webhook URL
          </Label>
          <Input
            placeholder="https://discord.com/api/webhooks/…"
            value={state.discord_webhook_url}
            onChange={(e) =>
              setState({ ...state, discord_webhook_url: e.target.value })
            }
            data-testid={TEST_IDS.integrationDiscordInput}
            className="mt-1 font-mono text-xs"
          />
          <div className="mt-3 flex justify-end">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => send("discord")}
              disabled={!state.discord_webhook_url}
              data-testid={TEST_IDS.integrationDiscordTest}
            >
              {t("integrations.send_test")}
            </Button>
          </div>
        </div>

        <div className="rounded-lg border border-zinc-200 bg-white p-6">
          <div className="mb-4 text-xs uppercase tracking-[0.2em] font-semibold text-zinc-500">
            {t("integrations.events_title")}
          </div>
          <ul className="divide-y divide-zinc-100">
            {EVENT_KEYS.map((k) => (
              <li key={k} className="flex items-center justify-between py-3">
                <div>
                  <div className="text-sm font-medium text-zinc-900">
                    {t(`notif.${k}`)}
                  </div>
                  <div className="text-xs text-zinc-500">
                    {t(`notif.${k}_desc`)}
                  </div>
                </div>
                <Switch
                  checked={state.events.includes(k)}
                  onCheckedChange={() => toggleEvent(k)}
                  data-testid={TEST_IDS.integrationEventToggle(k)}
                />
              </li>
            ))}
          </ul>
        </div>

        <div className="flex justify-end">
          <Button
            type="submit"
            data-testid={TEST_IDS.integrationSaveBtn}
            className="bg-zinc-900 text-white hover:bg-zinc-800"
          >
            {t("projects.save")}
          </Button>
        </div>
      </form>
    </div>
  );
}
